/**
 * Main-thread client for the Sapling signer Web Worker.
 *
 * Each `signInWorker` call spawns a fresh worker, sends the request,
 * awaits one response, and terminates. We don't reuse workers across
 * sends because Sapling proving spikes WASM heap to ~150–200 MB; if a
 * second send hits that worker before the first's heap is GC'd we'd
 * peak near the iOS WKWebView per-process memory ceiling on older
 * iPhones.
 */

import type { SignRequest, SignResponse } from "./sign-worker";

export interface BuiltTx {
  tx_hex: string;
  txid: string;
  fee_zats: number;
  spent_nullifiers: string[];
}

let nextId = 1;

export async function signInWorker(args: {
  signArgsJson: string;
  spendParams: Uint8Array;
  outputParams: Uint8Array;
}): Promise<BuiltTx> {
  const id = nextId++;
  const worker = new Worker(
    new URL("./sign-worker.ts", import.meta.url),
    { type: "module" },
  );

  try {
    const result = await new Promise<unknown>((resolve, reject) => {
      const onMessage = (ev: MessageEvent<SignResponse>) => {
        if (ev.data.id !== id) return;
        worker.removeEventListener("message", onMessage);
        worker.removeEventListener("error", onError);
        if (ev.data.kind === "ok") resolve(ev.data.result);
        else reject(new Error(ev.data.message));
      };
      const onError = (e: ErrorEvent) => {
        worker.removeEventListener("message", onMessage);
        worker.removeEventListener("error", onError);
        reject(new Error(e.message || "sign worker crashed"));
      };
      worker.addEventListener("message", onMessage);
      worker.addEventListener("error", onError);

      const req: SignRequest = {
        id,
        signArgsJson: args.signArgsJson,
        spendParams: args.spendParams,
        outputParams: args.outputParams,
      };
      worker.postMessage(req, [
        req.spendParams.buffer,
        req.outputParams.buffer,
      ]);
    });
    return result as BuiltTx;
  } finally {
    worker.terminate();
  }
}
